import { Descriptions as AntdDescriptions, Tag } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import AuthContext from "../../../context/auth/context";
import { Permissions } from "../../../utils/Permissions/Permissions";
import ScaleContext from "../../../context/scale/context";
import ScaleContextProvider from "../../../context/scale/provider";

const userName = (user?: { firstName?: string; lastName?: string } | null) =>
  user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || "-" : "-";

interface Props {}

const ScaleDetailsComponent: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const { details, actions, loading } = useContext(ScaleContext);
  const { userPermissions: permissions } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();

  // Defining a device is installation work, so the edit button is offered only
  // to whoever carries that permission; the screen itself stays open to all.
  let userPermissions =
    permissions?.length > 0
      ? permissions
      : localStorage?.getItem("permisionStoroge")?.split(",");
  if (!userPermissions) userPermissions = [];
  const canUpdateScale = userPermissions.includes(Permissions.CAN_UPDATE_SCALE);

  useEffect(() => {
    const getDetails = async () => {
      id && (await actions.getDetails(Number(id)));
    };
    getDetails();
  }, [direction]);

  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("scale")}
        buttonName={canUpdateScale ? `${t("update_scale")}` : undefined}
        onClick={() => navigate(`/scales/update/${id}`)}
        loading={loading.includes("details")}
        onRefresh={() => actions.getDetails(Number(id))}
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <Descriptions size="small" column={1} bordered>
            <AntdDescriptions.Item label={t("id")}>
              {details?.id}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("name")}>
              {details?.name ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("store")}>
              {details?.store?.name ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("description")}>
              {details?.description ?? "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("slots_count")}>
              {details?.slotsCount ? (
                details.slotsCount
              ) : (
                <Tag icon={<WarningOutlined />} color="error">
                  {t("scale_capacity_not_set")}
                </Tag>
              )}
            </AntdDescriptions.Item>

            {/* The device reads two digits, so the screen shows two digits. */}
            <AntdDescriptions.Item label={t("department")}>
              {String(details?.department ?? 0).padStart(2, "0")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("products_count")}>
              {details?.productsCount ?? 0}
            </AntdDescriptions.Item>

            {/* Downloading and feeding are two jobs, often two people. */}
            <AntdDescriptions.Item label={t("scale_last_fed_at")}>
              {details?.lastExportedAt
                ? `${new Date(details.lastExportedAt).toLocaleString()} - ${userName(details?.lastExportedBy)}`
                : t("scale_never_fed")}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("scale_downloaded_at")}>
              {details?.pendingExportAt
                ? `${new Date(details.pendingExportAt).toLocaleString()} - ${userName(details?.pendingExportBy)}`
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("is_active")}>
              {details?.isActive ? (
                <Tag icon={<CheckCircleOutlined />} color="success">
                  {t("active")}
                </Tag>
              ) : (
                <Tag icon={<CloseCircleOutlined />} color="default">
                  {t("inactive")}
                </Tag>
              )}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("created_at")}>
              {details?.createdAt
                ? new Date(details.createdAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("created_by")}>
              {details?.createdBy
                ? `${details.createdBy.firstName ?? ""} ${details.createdBy.lastName ?? ""}`.trim() ||
                  "-"
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("updated_at")}>
              {details?.updatedAt
                ? new Date(details.updatedAt).toLocaleString()
                : "-"}
            </AntdDescriptions.Item>

            <AntdDescriptions.Item label={t("updated_by")}>
              {details?.updatedBy
                ? `${details.updatedBy.firstName ?? ""} ${details.updatedBy.lastName ?? ""}`.trim() ||
                  "-"
                : "-"}
            </AntdDescriptions.Item>
          </Descriptions>
        )}
      </PageDetails>
    </>
  );
};

const ScaleDetailsPage = () => {
  return (
    <ScaleContextProvider>
      <ScaleDetailsComponent />
    </ScaleContextProvider>
  );
};

export default ScaleDetailsPage;
